import type { PlayingCard } from '@/types'
import { shuffleArray } from '@/lib/utils'

// Create a deck of cards (6 decks like real casino)
export function createDeck(): PlayingCard[] {
  const suits: PlayingCard['suit'][] = ['♠', '♥', '♦', '♣']
  const values: PlayingCard['value'][] = ['A', '2', '3', '4', '5', '6', '7', '8', '9', '10', 'J', 'Q', 'K']
  const deck: PlayingCard[] = []

  for (let d = 0; d < 6; d++) {
    for (const suit of suits) {
      for (const value of values) {
        deck.push({
          suit,
          value,
          color: (suit === '♥' || suit === '♦') ? 'red' : 'black',
        })
      }
    }
  }

  return shuffleArray(deck)
}

// Calculate hand value with Ace handling
export function calculateHandValue(hand: PlayingCard[]): number {
  let value = 0
  let aces = 0

  for (const card of hand) {
    if (card.value === 'A') {
      aces++
      value += 11
    } else if (['K', 'Q', 'J'].includes(card.value)) {
      value += 10
    } else {
      value += parseInt(card.value)
    }
  }

  // Adjust aces if over 21
  while (value > 21 && aces > 0) {
    value -= 10
    aces--
  }

  return value
}

// Check if hand is blackjack (21 with 2 cards)
export function isBlackjack(hand: PlayingCard[]): boolean {
  return hand.length === 2 && calculateHandValue(hand) === 21
}

// Check if hand is busted
export function isBusted(hand: PlayingCard[]): boolean {
  return calculateHandValue(hand) > 21
}

// Check if player can split (two cards with same value)
export function canSplit(hand: PlayingCard[]): boolean {
  if (hand.length !== 2) return false
  const val1 = getCardPointValue(hand[0])
  const val2 = getCardPointValue(hand[1])
  return val1 === val2
}

// Get card point value for split comparison
export function getCardPointValue(card: PlayingCard): number {
  if (['K', 'Q', 'J'].includes(card.value)) return 10
  if (card.value === 'A') return 11
  return parseInt(card.value)
}

// Determine winner
export type GameResult = 'blackjack' | 'win' | 'lose' | 'push' | 'bust'

export function determineWinner(
  playerValue: number,
  dealerValue: number,
  playerHasBlackjack: boolean,
  dealerHasBlackjack: boolean
): GameResult {
  // Blackjack scenarios
  if (playerHasBlackjack && dealerHasBlackjack) return 'push'
  if (playerHasBlackjack) return 'blackjack'
  if (dealerHasBlackjack) return 'lose'

  // Bust scenarios
  if (playerValue > 21) return 'bust'
  if (dealerValue > 21) return 'win'

  // Compare values
  if (playerValue > dealerValue) return 'win'
  if (playerValue < dealerValue) return 'lose'
  return 'push'
}

// Calculate payout
export function calculatePayout(result: GameResult, betAmount: number): number {
  switch (result) {
    case 'blackjack':
      return betAmount * 2.5 // Blackjack pays 3:2
    case 'win':
      return betAmount * 2
    case 'push':
      return betAmount // Return bet
    case 'lose':
    case 'bust':
      return 0
  }
}
